import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { fetchFromOldApi } from '~/services/Http';

import { Employee, EmployeeLoginData, EmploymentData } from './types';

export type StoreState = {
  employee: Employee;
  isLoading: boolean;
  failedToLoad: boolean;
  hasData: boolean;

  employeeEmploymentData: EmploymentData[];
  employmentDataLoading: boolean;
  employmentDataFailedLoading: boolean;
  hasEmploymentData: boolean;

  employeeAdminLoginData: EmployeeLoginData;
  loginDataLoading: boolean;
  loginDataFailedLoading: boolean;
  hasLoginData: boolean;
};

const initialState: StoreState = {
  employee: {} as Employee,
  isLoading: false,
  failedToLoad: false,
  hasData: false,

  employeeEmploymentData: [],
  employmentDataLoading: false,
  employmentDataFailedLoading: false,
  hasEmploymentData: false,

  employeeAdminLoginData: {} as EmployeeLoginData,
  loginDataLoading: false,
  loginDataFailedLoading: false,
  hasLoginData: false,
};

export enum EmployeeActionKeyPrefixes {
  FETCH_EMPLOYEE_DATA = 'employee/FETCH_EMPLOYEE_DATA',
  FETCH_EMPLOYEE_LOGIN_DATA = 'employee/FETCH_EMPLOYEE_LOGIN_DATA',
  FETCH_EMPLOYEE_EMPLOYMENT_DATA = 'employee/FETCH_EMPLOYEE_EMPLOYMENT_DATA',
  UPDATE_USER_LOGIN_METHOD = 'employee/UPDATE_USER_LOGIN_METHOD',
  UPDATE_USER_USERNAME = 'employee/UPDATE_USER_USERNAME',
}

export type EmployeePayload = {
  employee: Employee;
};

export type EmployeeEmploymentPayload = {
  employeeEmploymentData: EmploymentData[];
};

export type EmployeeLoginPayload = {
  employeeAdminLoginData: EmployeeLoginData;
};

export const getEmployeeData = (empId: number | string): Promise<Employee> =>
  fetchFromOldApi(`/json/employee/${empId}/full`).then((r) => r.json());

export const getUserLoginData = (
  username: string,
): Promise<EmployeeLoginData> =>
  fetchFromOldApi(`/json/user/${username}`).then((r) => r.json());

export const getEmpUserName = (
  empId: number | string,
): Promise<string | null> => {
  return fetchFromOldApi(`/json/employee/${empId}/username`).then((r) =>
    r.json(),
  );
};

export const getEmploymentData = (
  empId: number | string,
): Promise<EmploymentData[]> =>
  fetchFromOldApi(`/json/employee/${empId}/employment`).then((r) => r.json());

export const setUserLoginMethod = (
  userName: string,
  loginIsExternal: boolean,
): Promise<boolean> =>
  fetchFromOldApi(`/json/user/${userName}/external`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(loginIsExternal),
  }).then((r) => r.json());

export const setUserUsername = (
  userName: string,
  newUserName: string,
): Promise<string> =>
  fetchFromOldApi(`/json/user/${userName}/username`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(newUserName),
  }).then((r) => r.json());

type FetchEmployeeProps = {
  empId: number | string;
};

export const fetchEmployee = createAsyncThunk<
  EmployeePayload,
  FetchEmployeeProps
>(
  EmployeeActionKeyPrefixes.FETCH_EMPLOYEE_DATA,
  async ({ empId }): Promise<EmployeePayload> => {
    return {
      employee: await getEmployeeData(empId),
    };
  },
);

export const fetchEmployeeEmployment = createAsyncThunk<
  EmployeeEmploymentPayload,
  FetchEmployeeProps
>(
  EmployeeActionKeyPrefixes.FETCH_EMPLOYEE_EMPLOYMENT_DATA,
  async ({ empId }): Promise<EmployeeEmploymentPayload> => {
    return {
      employeeEmploymentData: await getEmploymentData(empId),
    };
  },
);

export const fetchEmployeeLoginData = createAsyncThunk<
  EmployeeLoginPayload,
  FetchEmployeeProps
>(
  EmployeeActionKeyPrefixes.FETCH_EMPLOYEE_LOGIN_DATA,
  async ({ empId }): Promise<EmployeeLoginPayload> => {
    const employeeUserName = await getEmpUserName(empId);

    if (employeeUserName === null) {
      return Promise.reject(
        `fetchEmployeeLoginData: No username found for employeeId ${empId}`,
      );
    }

    return {
      employeeAdminLoginData: await getUserLoginData(employeeUserName),
    };
  },
);

type UpdateUserLoginMethodPayload = {
  employeeAdminLoginData: Pick<EmployeeLoginData, 'fLoginIsExternal'>;
};

type UpdateUserLoginMethodProps = {
  employeeId: number | string;
  loginIsExternal: boolean;
};

export const updateUserLoginMethod = createAsyncThunk<
  UpdateUserLoginMethodPayload,
  UpdateUserLoginMethodProps
>(
  EmployeeActionKeyPrefixes.UPDATE_USER_LOGIN_METHOD,
  async ({
    employeeId,
    loginIsExternal,
  }): Promise<UpdateUserLoginMethodPayload> => {
    const employeeUserName = await getEmpUserName(employeeId);

    if (employeeUserName === null) {
      return Promise.reject(
        `updateUserLoginMethod: No username found for employeeId ${employeeId}`,
      );
    }

    return {
      employeeAdminLoginData: {
        fLoginIsExternal: await setUserLoginMethod(
          employeeUserName,
          loginIsExternal,
        ),
      },
    };
  },
);

type UpdateUserUsernamePayload = {
  employeeAdminLoginData: Pick<EmployeeLoginData, 'fLoginUsername'>;
};

type UpdateUserUsernameProps = {
  employeeId: number | string;
  newUserName: string;
};

export const updateUserUserName = createAsyncThunk<
  UpdateUserUsernamePayload,
  UpdateUserUsernameProps
>(
  EmployeeActionKeyPrefixes.UPDATE_USER_USERNAME,
  async ({ employeeId, newUserName }): Promise<UpdateUserUsernamePayload> => {
    const employeeUserName = await getEmpUserName(employeeId);

    if (employeeUserName === null) {
      return Promise.reject(
        `updateUserUserName: No username found for employeeId ${employeeId}`,
      );
    }

    return {
      employeeAdminLoginData: {
        fLoginUsername: await setUserUsername(employeeUserName, newUserName),
      },
    };
  },
);

export const employeeSlice = createSlice({
  name: '~/data/employee',
  initialState,
  reducers: {},
  extraReducers: {
    [fetchEmployee.fulfilled.type]: (
      state: StoreState,
      action: PayloadAction<EmployeePayload>,
    ) => {
      state.failedToLoad = false;
      state.isLoading = false;
      state.hasData = true;
      state.employee = action.payload.employee;
    },
    [fetchEmployee.pending.type]: (state: StoreState) => {
      state.isLoading = true;
    },
    [fetchEmployee.rejected.type]: (state: StoreState) => {
      state.failedToLoad = true;
    },
    [fetchEmployeeEmployment.fulfilled.type]: (
      state: StoreState,
      action: PayloadAction<EmployeeEmploymentPayload>,
    ) => {
      state.employmentDataLoading = false;
      state.employmentDataFailedLoading = false;
      state.hasEmploymentData = true;
      state.employeeEmploymentData = action.payload.employeeEmploymentData;
    },
    [fetchEmployeeEmployment.pending.type]: (state: StoreState) => {
      state.employmentDataLoading = true;
    },
    [fetchEmployeeEmployment.rejected.type]: (state: StoreState) => {
      state.employmentDataFailedLoading = true;
    },
    [fetchEmployeeLoginData.fulfilled.type]: (
      state: StoreState,
      action: PayloadAction<EmployeeLoginPayload>,
    ) => {
      state.loginDataLoading = false;
      state.loginDataFailedLoading = false;
      state.hasLoginData = true;
      state.employeeAdminLoginData = action.payload.employeeAdminLoginData;
    },
    [fetchEmployeeLoginData.pending.type]: (state: StoreState) => {
      state.loginDataLoading = true;
    },
    [fetchEmployeeLoginData.rejected.type]: (state: StoreState) => {
      state.loginDataFailedLoading = true;
    },
    [updateUserLoginMethod.fulfilled.type]: (
      state: StoreState,
      action: PayloadAction<UpdateUserLoginMethodPayload>,
    ) => {
      state.loginDataLoading = false;
      state.loginDataFailedLoading = false;
      state.hasLoginData = true;
      state.employeeAdminLoginData = {
        ...state.employeeAdminLoginData,
        ...action.payload.employeeAdminLoginData,
      };
    },
    [updateUserLoginMethod.pending.type]: (state: StoreState) => {
      state.loginDataLoading = true;
    },
    [updateUserLoginMethod.rejected.type]: (state: StoreState) => {
      state.loginDataFailedLoading = true;
    },
    [updateUserUserName.fulfilled.type]: (
      state: StoreState,
      action: PayloadAction<UpdateUserUsernamePayload>,
    ) => {
      state.loginDataLoading = false;
      state.loginDataFailedLoading = false;
      state.hasLoginData = true;
      state.employeeAdminLoginData = {
        ...state.employeeAdminLoginData,
        ...action.payload.employeeAdminLoginData,
      };
    },
    [updateUserUserName.pending.type]: (state: StoreState) => {
      state.loginDataLoading = true;
    },
    [updateUserUserName.rejected.type]: (state: StoreState) => {
      state.loginDataFailedLoading = true;
    },
  },
});

export const employeeReducer = employeeSlice.reducer;
